import { BarChart, StatTile } from '../charts/Charts'

export interface ExecSummaryData {
  cycle: {
    full_laps: number | null
    typical_lap_hours: number | null
    typical_hub_hours: number | null
    typical_blended_kwh_per_km: number | null
    verdict: string | null
  }
  trend: Array<{ site: string; day: string; arrivals: number; departures: number }>
  median_tat_min: number | null
  resolved_count: number
  alert_kinds: Array<{ kind: string; n: number; crit: number }>
  total_alerts: number
  total_critical: number
  top_vehicles: Array<{ vehicle_id: string; n: number }>
  soh: { avg_soh: number | null; total: number; below_70: number }
  trips_lost: number
  revenue_lost_inr: number
}

function fmtH(h: number | null): string {
  return h == null ? '—' : `${h}`
}

export function ExecSummaryPreview({ data }: { data: ExecSummaryData }): JSX.Element {
  const byDay = new Map<string, { arrivals: number; departures: number }>()
  for (const r of data.trend) {
    const cur = byDay.get(r.day) ?? { arrivals: 0, departures: 0 }
    cur.arrivals += r.arrivals
    cur.departures += r.departures
    byDay.set(r.day, cur)
  }
  const trendPoints = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, v]) => ({
      label: day.slice(5), title: day,
      values: { arrivals: v.arrivals, departures: v.departures },
    }))

  const alertPoints = data.alert_kinds.slice(0, 6).map((a) => ({
    label: a.kind.replace(/_/g, ' ').toLowerCase(),
    title: a.kind,
    values: { total: a.n, critical: a.crit },
  }))

  const bySite = new Map<string, { arrivals: number; departures: number }>()
  for (const r of data.trend) {
    const cur = bySite.get(r.site) ?? { arrivals: 0, departures: 0 }
    cur.arrivals += r.arrivals
    cur.departures += r.departures
    bySite.set(r.site, cur)
  }
  const sitePoints = [...bySite.entries()]
    .sort(([, a], [, b]) => (b.arrivals + b.departures) - (a.arrivals + a.departures))
    .map(([site, v]) => ({ label: site, title: site, values: { arrivals: v.arrivals, departures: v.departures } }))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <StatTile label="Full round trips" value={data.cycle.full_laps ?? '—'} />
        <StatTile label="Typical round trip" value={fmtH(data.cycle.typical_lap_hours)} unit="h" />
        <StatTile label="Typical hub time" value={fmtH(data.cycle.typical_hub_hours)} unit="h" />
        <StatTile label="Energy" value={fmtH(data.cycle.typical_blended_kwh_per_km)} unit="kWh/km" />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Arrivals vs departures, by day</h5>
          <BarChart
            data={trendPoints}
            series={[
              { key: 'arrivals', label: 'Arrivals', color: '#2563EB' },
              { key: 'departures', label: 'Departures', color: '#94A3B8' },
            ]}
          />
        </div>
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Alerts by kind</h5>
          <BarChart
            data={alertPoints}
            series={[
              { key: 'total', label: 'Total', color: '#0EA5E9' },
              { key: 'critical', label: 'Critical', color: '#DC2626' },
            ]}
          />
        </div>
      </div>

      <div className="rounded-lg border border-slate-100 p-3">
        <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Arrivals vs departures, by hub</h5>
        <BarChart
          data={sitePoints}
          series={[
            { key: 'arrivals', label: 'Arrivals', color: '#2563EB' },
            { key: 'departures', label: 'Departures', color: '#94A3B8' },
          ]}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Top vehicles by alert count</h5>
          {data.top_vehicles.length === 0 ? (
            <p className="text-[11px] text-slate-400">No alerts in this window.</p>
          ) : (
            <ul className="space-y-1">
              {data.top_vehicles.map((v) => (
                <li key={v.vehicle_id} className="flex items-center justify-between text-[11.5px]">
                  <span className="font-mono text-slate-700">{v.vehicle_id}</span>
                  <span className="font-mono tabular-nums text-slate-500">{v.n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-lg border border-slate-100 p-3">
          <h5 className="mb-2 text-[11px] font-semibold text-slate-700">Breakdown TAT &amp; fleet SoH</h5>
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Median TAT" value={data.median_tat_min != null ? Math.round(data.median_tat_min / 60) : '—'} unit="h" />
            <StatTile label="Resolved" value={data.resolved_count} />
            <StatTile label="Avg SoH" value={data.soh.avg_soh != null ? Math.round(data.soh.avg_soh) : '—'} unit="%" />
            <StatTile label="Below 70%" value={data.soh.below_70} tone={data.soh.below_70 > 0 ? 'warn' : 'default'} />
          </div>
        </div>
      </div>

      {data.cycle.verdict && (
        <p className="rounded-md bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">{data.cycle.verdict}</p>
      )}
    </div>
  )
}
